import React from "react";

/**
 * ExportTableToCSV
 * Props:
 * - tableId (string) - id của table trên DOM (bắt buộc)
 * - filename (string) - tên file xuất (mặc định: table.csv)
 * - className (string) - class cho button
 * - buttonText (string) - text hiển thị trên button
 * - delimiter (string) - dấu phân cách, mặc định là ','
 * - includeHeaders (boolean) - có xuất header (th) hay không (mặc định: true)
 *
 * Mục tiêu: cách dùng giống `react-html-table-to-excel` nhưng xuất file .csv
 * từ chính table hiện có trên UI.
 */

export default function ExportTableToCSV({
    tableId,
    filename = "table.csv",
    className = "export-csv-button",
    buttonText = "Export CSV",
    delimiter = ",",
    includeHeaders = true,
    disabledBtn = false
}) {
    if (!tableId) {
        throw new Error("ExportTableToCSV: prop 'tableId' is required");
    }

    const sanitizeCell = (text) => {
        if (text == null) return '""';
        let s = String(text).replace(/\r?\n/g, " ").trim();
        const escaped = s.replace(/"/g, '""');

        // Các pattern mà Excel hay tự chuyển:
        const riskyRange = /^\s*\d+\s*-\s*\d+\s*$/;           // 0-3, 3 - 6, ...
        const riskyDateLike = /^\s*\d{1,4}[\/\-]\d{1,2}([\/\-]\d{1,4})?\s*$/;
        const startsWithZeroNumber = /^\s*0\d+/;              // mã zip bắt đầu bằng 0

        // Nếu rủi ro — trả cell dạng ="text" để Excel bắt làm TEXT
        if (riskyRange.test(s) || riskyDateLike.test(s) || startsWithZeroNumber.test(s)) {
            return `="${escaped}"`;
        }

        // Trường hợp bình thường: đóng dấu "..." cho CSV
        return `"${escaped}"`;
    };

    const buildCSV = (tableEl) => {
        const rows = Array.from(tableEl.querySelectorAll("tr"));
        const lines = [];

        for (const row of rows) {
            // Skip rows that are visually hidden
            const style = window.getComputedStyle(row);
            if (style && style.display === "none") continue;

            const cells = Array.from(row.querySelectorAll("th, td"));
            // If header is disabled and this is a header row, skip it
            const isHeaderRow = Array.from(row.children).some((c) => c.tagName.toLowerCase() === "th");
            if (!includeHeaders && isHeaderRow) continue;

            const values = cells.map((cell) => sanitizeCell(cell.innerText));
            lines.push(values.join(delimiter));
        }

        return lines.join("\r\n");
    };

    const handleClick = (e) => {
        e && e.preventDefault();

        const tableEl = document.getElementById(tableId);
        if (!tableEl) {
            console.error(`ExportTableToCSV: Không tìm thấy table có id='${tableId}'`);
            return;
        }

        const csvContent = buildCSV(tableEl);

        // Add BOM so Excel (Windows) sẽ mở UTF-8 đúng với tiếng Việt
        const bom = "\uFEFF";
        const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });

        // tạo link download
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <button type="button" className={className} onClick={handleClick} disabled={disabledBtn}>
            {buttonText}
        </button>
    )
}

/**
 * Example usage (copy vào component khác):
 *
 * <table id="ExportTable">
 *   <thead>
 *     <tr><th>Tên</th><th>Tuổi</th></tr>
 *   </thead>
 *   <tbody>
 *     <tr><td>Jon</td><td>30</td></tr>
 *   </tbody>
 * </table>
 *
 * <ExportTableToCSV
 *   tableId="ExportTable"
 *   filename={`production-${new Date().toISOString().slice(0,19)}.csv`}
 *   className="btn btn-primary"
 *   buttonText="Export"
 *   delimiter=","  // hoặc ';' nếu muốn
 *   includeHeaders={true}
 * />
 */
